import { Injectable, inject, Optional } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { BehaviorSubject, Observable, from, of, switchMap } from 'rxjs';
import { catchError, map, tap, filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { FcmService } from './fcm.service';
import { PlatformService } from './platform.service';

export interface PushNotificationMessage {
  title?: string;
  body?: string;
  data?: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private messaging = inject(Messaging, { optional: true });
  private fcmService = inject(FcmService);
  private platform = inject(PlatformService);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private messageSubject = new BehaviorSubject<PushNotificationMessage | null>(null);

  /** Observable that emits the current FCM token */
  token$ = this.tokenSubject.asObservable();
  
  /** Observable that emits incoming foreground messages */
  message$ = this.messageSubject.asObservable();

  /** Observable that emits the registered token from backend */
  registeredToken$ = this.fcmService.registeredToken$;

  constructor() {
    // Auto-initialize if Firebase is configured
    if (this.messaging) {
      this.setupForegroundMessageListener();
      console.log('Firebase Cloud Messaging initialized via Angular Fire');
    } else {
      console.warn('Firebase Messaging not available - check Firebase configuration');
    }
  }

  /**
   * Request notification permission and get FCM token
   * @returns Observable that emits the FCM token if permission is granted
   */
  requestPermissionAndGetToken(): Observable<string | null> {
    if (!this.messaging) {
      console.error('Firebase Messaging not initialized');
      return of(null);
    }

    return from(Notification.requestPermission()).pipe(
      map(permission => {
        if (permission === 'granted') {
          console.log('Notification permission granted.');
          return permission;
        }
        console.log('Notification permission denied.');
        return null;
      }),
      catchError(error => {
        console.error('Error requesting notification permission:', error);
        return of(null);
      })
    );
  }

  /**
   * Get the current FCM registration token
   * @returns Observable that emits the FCM token or null
   */
  getRegistrationToken(): Observable<string | null> {
    if (!this.messaging || !environment.firebase?.vapidKey) {
      console.error('Firebase Messaging not initialized or VAPID key missing');
      return of(null);
    }

    return from(getToken(this.messaging, {
      vapidKey: environment.firebase.vapidKey
    })).pipe(
      tap(token => {
        if (token) {
          console.log('FCM registration token obtained');
          this.tokenSubject.next(token);
        } else {
          console.log('No registration token available. Request permission first.');
        }
      }),
      catchError(error => {
        console.error('Error retrieving FCM token:', error);
        return of(null);
      })
    );
  }

  /**
   * Request permission and get token in one call
   * @returns Observable that emits the FCM token or null
   */
  requestAndGetToken(): Observable<string | null> {
    return new Observable(subscriber => {
      this.requestPermissionAndGetToken().subscribe({
        next: permission => {
          if (permission) {
            this.getRegistrationToken().subscribe({
              next: token => {
                subscriber.next(token);
                subscriber.complete();
              },
              error: err => subscriber.error(err)
            });
          } else {
            subscriber.next(null);
            subscriber.complete();
          }
        },
        error: err => subscriber.error(err)
      });
    });
  }

  /**
   * Request permission, get token, and register with backend in one call
   * This is the recommended method for enabling push notifications
   * @returns Observable that emits the FCM token or null
   */
  enablePushNotifications(): Observable<string | null> {
    return this.requestAndGetToken().pipe(
      switchMap(token => {
        if (token) {
          return this.fcmService.registerToken(token).pipe(
            map(response => response.success ? token : null)
          );
        }
        return of(null);
      }),
      tap(token => {
        if (token) {
          console.log('🔔 Push notifications enabled and registered');
        }
      })
    );
  }

  /**
   * Disable push notifications and unregister from backend
   * @returns Observable that completes when done
   */
  disablePushNotifications(): Observable<boolean> {
    const token = this.tokenSubject.value;
    if (!token) {
      return of(true);
    }
    
    return this.fcmService.unregisterToken(token).pipe(
      map(response => response.success),
      tap(success => {
        if (success) {
          this.tokenSubject.next(null);
          console.log('🔕 Push notifications disabled');
        }
      })
    );
  }

  /**
   * Setup listener for foreground messages
   * Background messages are handled by the service worker
   */
  private setupForegroundMessageListener(): void {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      const message: PushNotificationMessage = {
        title: payload.notification?.title,
        body: payload.notification?.body,
        data: payload.data
      };

      this.messageSubject.next(message);

      // Optionally show a notification manually for foreground messages
      if (Notification.permission === 'granted' && payload.notification) {
        this.showNotification(payload.notification.title || 'New Message', {
          body: payload.notification.body,
          icon: '/assets/icons/icon-192x192.png',
          data: payload.data
        });
      }
    });
  }

  /**
   * Manually show a notification (useful for foreground messages)
   */
  private showNotification(title: string, options?: NotificationOptions): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, options);
    }
  }

  /**
   * Check if notifications are supported in this browser
   */
  isSupported(): boolean {
    if (!this.platform.isBrowser) return false;
    const win = this.platform.window;
    return win ? 'Notification' in win && 'serviceWorker' in navigator && 'PushManager' in win : false;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Check if token is currently registered with backend
   */
  isRegisteredWithBackend(): boolean {
    return this.fcmService.isTokenRegistered();
  }

  /**
   * Get the FCM service for advanced operations (admin notifications, broadcasts, etc.)
   */
  getFcmService(): FcmService {
    return this.fcmService;
  }
}
